import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ADMIN_SECTIONS_STORAGE_KEY = 'intfinder-sidebar-admin-sections';

interface AdminMenuItem {
  path: string;
  label: string;
  icon: string;
  category: string;
}

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

// Icon constants for better maintainability
const ICONS = {
  dashboard: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  subscriptions: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  payments: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  apiKeys: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  referrals: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z',
  organizations: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  apiManagement: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  aiProviders: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  aiSummaryConfig: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  searchParameters: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  contentTransformer: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  apiResponses: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  sessionManager: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  monitoring: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  subscriptionPlans: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  paymentManagement: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  offlinePayments: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  intcoinsReport: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  osintTest: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  universalOsint: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  lawEnforcement: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  reportTemplates: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  reports: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  chevronLeft: 'M15 19l-7-7 7-7',
  chevronRight: 'M9 5l7 7-7 7',
  chevronDown: 'M19 9l-7 7-7-7',
  menu: 'M4 6h16M4 12h16M4 18h16',
  close: 'M6 18L18 6M6 6l12 12',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
} as const;

const ADMIN_CATEGORY_ICONS: Record<string, string> = {
  'User Management': ICONS.users,
  'Master Management': ICONS.settings,
  'API Monitoring': ICONS.monitoring,
  System: ICONS.dashboard,
  Billing: ICONS.payments,
  Tools: ICONS.osintTest,
};

const matchesMenuPath = (menuPath: string, pathname: string, search: string): boolean => {
  const [menuPathname, menuSearch] = menuPath.split('?');
  if (pathname !== menuPathname) return false;
  if (menuSearch) {
    const params = new URLSearchParams(menuSearch);
    const current = new URLSearchParams(search);
    return Array.from(params.keys()).every((key) => current.get(key) === params.get(key));
  }
  return !search;
};

// Reusable MenuItem component
interface MenuItemProps {
  item: {
    path: string;
    label: string;
    icon: string;
    badge?: string | null;
  };
  collapsed: boolean;
  isActive: boolean;
  nested?: boolean;
  onClick?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, collapsed, isActive, nested = false, onClick }) => (
  <li className="group relative">
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center py-2.5 text-sm font-medium rounded-md transition-all duration-200
        ${nested && !collapsed ? 'pl-9 pr-3' : 'px-3'}
        ${isActive
          ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-600 text-indigo-700 dark:text-indigo-300 shadow-sm'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300'
        }
        ${collapsed ? 'justify-center py-3' : ''}
        focus:outline-none focus:ring-2 focus:ring-indigo-500
      `}
      tabIndex={0}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
      title={collapsed ? item.label : undefined}
      style={{ minHeight: 44 }}
    >
      <svg 
        className={`${nested && !collapsed ? 'w-5 h-5' : 'w-6 h-6'} flex-shrink-0 transition-all duration-200 ${
          isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400'
        }`} 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
      </svg>
      {!collapsed && (
        <div className="ml-3 flex items-center justify-between w-full">
          <span>{item.label}</span>
          {item.badge && (
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded-full">
              {item.badge}
            </span>
          )}
        </div>
      )}
    </Link>
  </li>
);

interface CollapsibleAdminSectionProps {
  category: string;
  items: AdminMenuItem[];
  sidebarCollapsed: boolean;
  expanded: boolean;
  hasActiveItem: boolean;
  onToggle: () => void;
  isItemActive: (path: string) => boolean;
  onItemClick?: () => void;
}

const CollapsibleAdminSection: React.FC<CollapsibleAdminSectionProps> = ({
  category,
  items,
  sidebarCollapsed,
  expanded,
  hasActiveItem,
  onToggle,
  isItemActive,
  onItemClick,
}) => {
  if (sidebarCollapsed) {
    return (
      <>
        {items.map((item) => (
          <MenuItem
            key={item.path}
            item={item}
            collapsed
            isActive={isItemActive(item.path)}
            onClick={onItemClick}
          />
        ))}
      </>
    );
  }

  const categoryIcon = ADMIN_CATEGORY_ICONS[category] ?? ICONS.dashboard;

  return (
    <li className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        className={`
          w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors
          focus:outline-none focus:ring-2 focus:ring-indigo-500
          ${hasActiveItem
            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/60 dark:bg-indigo-900/10'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
          }
        `}
        aria-expanded={expanded}
        aria-controls={`admin-section-${category.replace(/\s+/g, '-')}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={categoryIcon} />
          </svg>
          <span className="truncate">{category}</span>
          <span className="text-[10px] font-medium normal-case text-gray-400 dark:text-gray-500">
            {items.length}
          </span>
        </span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.chevronDown} />
        </svg>
      </button>
      <ul
        id={`admin-section-${category.replace(/\s+/g, '-')}`}
        className={`mt-0.5 space-y-0.5 overflow-hidden transition-all duration-200 ${
          expanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {items.map((item) => (
          <MenuItem
            key={item.path}
            item={item}
            collapsed={false}
            nested
            isActive={isItemActive(item.path)}
            onClick={onItemClick}
          />
        ))}
      </ul>
    </li>
  );
};

// User Profile Component
interface UserProfileProps {
  user: any;
  collapsed: boolean;
  profileDropdown: boolean;
  onProfileClick: () => void;
  onProfileBlur: () => void;
  onNavigateToProfile: () => void;
  onLogout: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({
  user,
  collapsed,
  profileDropdown,
  onProfileClick,
  onProfileBlur,
  onNavigateToProfile,
  onLogout
}) => (
  <div className="mt-auto">
    <button
      className={`
        p-4 border-t border-gray-200 dark:border-gray-700 transition-all duration-200 w-full flex items-center 
        focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-gray-100 dark:bg-gray-800 relative
        ${profileDropdown ? 'justify-center' : ''}
      `}
      style={{ minHeight: 56 }}
      tabIndex={0}
      aria-label="User profile"
      onClick={onProfileClick}
      onBlur={onProfileBlur}
    >
      <div className="flex items-center w-full">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-base font-medium text-white">
              {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
        </div>
        {!collapsed && (
          <div className="ml-3 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.firstName} {user?.lastName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
          </div>
        )}
      </div>
      
      {/* Dropdown for desktop */}
      {profileDropdown && !collapsed && (
        <div className="absolute right-4 bottom-16 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 w-48 z-50">
          <button 
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 flex items-center"
            onClick={onNavigateToProfile}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.profile} />
            </svg>
            Profile
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-gray-800 flex items-center"
            onClick={onLogout}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      )}
    </button>
    <div className="text-xs text-gray-400 text-center py-2 border-t border-gray-100 bg-gray-50 dark:bg-gray-900">
      IntFinder v1.0.0 &copy; 2023-2025
    </div>
  </div>
);

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(ADMIN_SECTIONS_STORAGE_KEY);
      if (saved) return JSON.parse(saved) as Record<string, boolean>;
    } catch {
      // ignore invalid stored state
    }
    return {};
  });

  const isItemActive = useCallback(
    (path: string) => matchesMenuPath(path, location.pathname, location.search),
    [location.pathname, location.search]
  );

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
    setProfileDropdown(false);
  }, [logout, navigate]);

  // Memoized menu items for better performance
  const menuItems = useMemo(() => [
    { path: '/app/dashboard', label: 'Dashboard', icon: ICONS.dashboard, badge: null },
    { path: '/app/search', label: 'Search', icon: ICONS.search, badge: null },
    { path: '/app/reports', label: 'My Reports', icon: ICONS.reports, badge: null },
    { path: '/app/subscriptions', label: 'Subscription Plans', icon: ICONS.subscriptions, badge: null },
    { path: '/app/payments', label: 'Payment History', icon: ICONS.payments, badge: null },
    { path: '/app/api-keys', label: 'API Keys', icon: ICONS.apiKeys, badge: null },
    { path: '/app/referrals', label: 'Referrals', icon: ICONS.referrals, badge: null },
    { path: '/app/profile', label: 'Profile', icon: ICONS.profile, badge: null },
  ], []);

  const adminMenuItems = useMemo(() => [
    { path: '/app/admin/users', label: 'User Management', icon: ICONS.users, category: 'User Management' },
    { path: '/app/admin/organizations', label: 'Organization Management', icon: ICONS.organizations, category: 'User Management' },
    { path: '/app/admin/law-enforcement', label: 'Law Enforcement', icon: ICONS.lawEnforcement, category: 'User Management' },
    { path: '/app/admin/api-management', label: 'API Management', icon: ICONS.apiManagement, category: 'Master Management' },
    { path: '/app/admin/report-templates', label: 'Report Templates', icon: ICONS.reportTemplates, category: 'Master Management' },
    { path: '/app/admin/search-parameters', label: 'Search Parameters', icon: ICONS.searchParameters, category: 'Master Management' },
    { path: '/app/admin/ai-providers', label: 'AI Providers', icon: ICONS.aiProviders, category: 'Master Management' },
    { path: '/app/admin/ai-summary-configs', label: 'AI Summary Config', icon: ICONS.aiSummaryConfig, category: 'Master Management' },
    { path: '/app/admin/api-content-transformer', label: 'API Content Transformer', icon: ICONS.contentTransformer, category: 'Master Management' },
    { path: '/app/admin/subscription-plans', label: 'Subscription Plans', icon: ICONS.subscriptionPlans, category: 'Master Management' },
    { path: '/app/admin/api-responses', label: 'API Responses', icon: ICONS.apiResponses, category: 'API Monitoring' },
    { path: '/app/admin/api-responses?failed_only=true', label: 'Failed API Calls', icon: ICONS.apiResponses, category: 'API Monitoring' },
    { path: '/app/admin/api-logs', label: 'Application Logs', icon: ICONS.monitoring, category: 'API Monitoring' },
    { path: '/app/admin/all-searches', label: 'All Searches', icon: ICONS.search, category: 'System' },
    { path: '/app/admin/session-manager', label: 'Session Manager', icon: ICONS.sessionManager, category: 'System' },
    { path: '/app/admin/monitoring', label: 'System Monitoring', icon: ICONS.monitoring, category: 'System' },
    { path: '/app/admin/payments', label: 'Payment Management', icon: ICONS.paymentManagement, category: 'Billing' },
    { path: '/app/admin/offline-payments', label: 'Offline Payments', icon: ICONS.offlinePayments, category: 'Billing' },
    { path: '/app/admin/intcoins-report', label: 'IntCoins Report', icon: ICONS.intcoinsReport, category: 'Billing' },
    { path: '/app/admin/osint-test', label: 'OSINT Tools Test', icon: ICONS.osintTest, category: 'Tools' },
    { path: '/app/admin/universal-osINT', label: 'Universal OSINT', icon: ICONS.universalOsint, category: 'Tools' },
  ], []);

  // Check if user is admin
  const isAdmin = useMemo(() => user?.isStaff || user?.isSuperuser, [user?.isStaff, user?.isSuperuser]);

  // Group admin items by category (preserves menu order)
  const groupedAdminItems = useMemo(() => {
    const groups: Array<[string, AdminMenuItem[]]> = [];
    const indexByCategory = new Map<string, number>();

    adminMenuItems.forEach((item) => {
      const existingIndex = indexByCategory.get(item.category);
      if (existingIndex === undefined) {
        indexByCategory.set(item.category, groups.length);
        groups.push([item.category, [item]]);
      } else {
        groups[existingIndex][1].push(item);
      }
    });

    return groups;
  }, [adminMenuItems]);

  const isSectionExpanded = useCallback(
    (category: string, items: AdminMenuItem[]) => {
      if (expandedSections[category] !== undefined) {
        return expandedSections[category];
      }
      return items.some((item) => isItemActive(item.path));
    },
    [expandedSections, isItemActive]
  );

  const toggleSection = useCallback((category: string, items: AdminMenuItem[]) => {
    setExpandedSections((prev) => {
      const currentlyExpanded = prev[category] ?? items.some((item) => isItemActive(item.path));
      const next = { ...prev, [category]: !currentlyExpanded };
      localStorage.setItem(ADMIN_SECTIONS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, [isItemActive]);

  useEffect(() => {
    const activeCategory = adminMenuItems.find((item) => isItemActive(item.path))?.category;
    if (!activeCategory) return;

    setExpandedSections((prev) => {
      if (prev[activeCategory]) return prev;
      const next = { ...prev, [activeCategory]: true };
      localStorage.setItem(ADMIN_SECTIONS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, [adminMenuItems, isItemActive]);

  // Check if mobile
  const isMobile = useMemo(() => window.innerWidth < 768, []);

  // Sidebar classes
  const sidebarBase = 'flex flex-col bg-white dark:bg-gray-900 shadow-lg pt-safe-top pb-safe-bottom custom-scrollbar transition-all duration-300 ease-in-out z-40';
  const sidebarWidth = collapsed ? 'w-20' : 'w-64';
  const sidebarFixed = 'fixed top-0 left-0 h-screen';

  // Mobile sidebar
  if (mobileOpen) {
    return (
      <div className="fixed inset-0 z-50 flex">
        <div className={`${sidebarBase} w-64 ${sidebarFixed}`}>
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 border-b border-indigo-100 dark:border-gray-800">
            <div className="flex items-center">
              <img 
                src="/logos/png/logo-no-background.png" 
                alt="IntFinder" 
                className="h-8 w-8 object-contain"
              />
              <span className="ml-3 font-bold text-lg text-indigo-700 dark:text-indigo-400">
                IntFinder
              </span>
            </div>
            <button 
              onClick={onCloseMobile} 
              className="p-2 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.close} />
              </svg>
            </button>
          </div>

          {/* User Info */}
          <div className="flex flex-col items-center py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-white">
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="mt-2 font-semibold text-gray-900 dark:text-white">{user?.firstName} {user?.lastName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-2 overflow-y-auto focus:outline-none custom-scrollbar" tabIndex={0}>
            <ul className="space-y-1">
              {menuItems.map((item) => (
                <MenuItem
                  key={item.path}
                  item={item}
                  collapsed={false}
                  isActive={isItemActive(item.path)}
                  onClick={onCloseMobile}
                />
              ))}
            </ul>

            {/* Admin Section */}
            {isAdmin && (
              <>
                <div className="my-6 border-t border-gray-200 dark:border-gray-700" />
                <div className="mb-3 px-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admin Panel</span>
                  {user?.isSuperuser && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                      Superadmin
                    </span>
                  )}
                </div>
                <ul className="space-y-1">
                  {groupedAdminItems.map(([category, items]) => (
                    <CollapsibleAdminSection
                      key={category}
                      category={category}
                      items={items}
                      sidebarCollapsed={false}
                      expanded={isSectionExpanded(category, items)}
                      hasActiveItem={items.some((item) => isItemActive(item.path))}
                      onToggle={() => toggleSection(category, items)}
                      isItemActive={isItemActive}
                      onItemClick={onCloseMobile}
                    />
                  ))}
                </ul>
              </>
            )}
          </nav>

          {/* User Profile */}
          <UserProfile
            user={user}
            collapsed={false}
            profileDropdown={false}
            onProfileClick={() => navigate('/app/profile')}
            onProfileBlur={() => {}}
            onNavigateToProfile={() => navigate('/app/profile')}
            onLogout={handleLogout}
          />
        </div>
        <div className="flex-1 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onCloseMobile} />
      </div>
    );
  }

  // Desktop sidebar
  return (
    <div className={`${sidebarBase} ${sidebarWidth} ${sidebarFixed}`}>
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 border-b border-indigo-100 dark:border-gray-800">
        {!collapsed && (
          <div className="flex items-center">
            <img 
              src="/logos/png/logo-no-background.png" 
              alt="IntFinder" 
              className="h-8 w-8 object-contain"
            />
            <span className="ml-3 font-bold text-lg text-indigo-700 dark:text-indigo-400">
              IntFinder
            </span>
          </div>
        )}
        
        {/* Collapse/expand button */}
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          tabIndex={0}
          style={{ minWidth: 44, minHeight: 44 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={collapsed ? ICONS.chevronRight : ICONS.chevronLeft} />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto focus:outline-none custom-scrollbar" tabIndex={0} aria-label="Main navigation">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <MenuItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              isActive={isItemActive(item.path)}
            />
          ))}
        </ul>

        {/* Admin Section */}
        {isAdmin && (
          <>
            <div className="my-6 border-t border-gray-200 dark:border-gray-700" />
            {!collapsed && (
              <div className="mb-3 px-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admin Panel</span>
                {user?.isSuperuser && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                    Superadmin
                  </span>
                )}
              </div>
            )}
            <ul className="space-y-1">
              {groupedAdminItems.map(([category, items]) => (
                <CollapsibleAdminSection
                  key={category}
                  category={category}
                  items={items}
                  sidebarCollapsed={collapsed}
                  expanded={isSectionExpanded(category, items)}
                  hasActiveItem={items.some((item) => isItemActive(item.path))}
                  onToggle={() => toggleSection(category, items)}
                  isItemActive={isItemActive}
                />
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* User Profile */}
      <UserProfile
        user={user}
        collapsed={collapsed}
        profileDropdown={profileDropdown}
        onProfileClick={() => {
          if (isMobile) {
            navigate('/app/profile');
          } else {
            setProfileDropdown((d) => !d);
          }
        }}
        onProfileBlur={() => setTimeout(() => setProfileDropdown(false), 150)}
        onNavigateToProfile={() => navigate('/app/profile')}
        onLogout={handleLogout}
      />
    </div>
  );
}; 